import { useState, FC, useMemo } from 'react'
import { PolicyTypeOptions } from 'src/dtos/applicationData'
import { QuoteDataWithId } from 'src/dtos/custom'
import { 
  QuoteDetailBoxContainer, 
  FlexDiv, 
  QuoteDetailBoxContentDiv,
  FormInputTitle,
  Chip
} from './styles'
import { Link } from '../defaultComponents'
import { LeftBar } from '../../basic/LeftBar'
import StandardSelect from '../../basic/StandardSelect'
import StandardRadio from '../../basic/StandardRadio'
import {
  regularPolicyDeductibles,
  PolicyLimitsLiabilityType,
  liabilityLimitsPolicies,
  policiesFeaturesIncluded
} from 'src/constants/application'
import { numberToCurrency } from 'src/utils/numberFormat'
import lightbulb from 'src/assets/lightbulb.svg'
import { IoIosCheckmarkCircle } from 'react-icons/io'
import { tlieSources } from 'src/constants/tlie'
import { AttorneyQuoteBoxSchema } from 'src/yup/NewAppSchema'

interface QuoteDetailBoxProps {
  quote: QuoteDataWithId;
  setQuote: (quote: QuoteDataWithId) => void;
  showErrors?: boolean;
  errorCodes?: string[]
}

const getPolicyType = (policyType: PolicyTypeOptions | undefined) : PolicyTypeOptions => 
{
  return  policyType === 'REGULAR' ? policyType : 'BASIC'
}
  


const findLiability = (limits: string | undefined, policyType: PolicyTypeOptions | undefined) => {
  if(limits && policyType) {
    const [perClaim, aggregate] = limits.split(';')

    return liabilityLimitsPolicies[policyType]
      .find(policy => policy.perClaim === Number(perClaim) && policy.aggregate === Number(aggregate))
  }
  return undefined
}

const QuoteDetailBox: FC<QuoteDetailBoxProps> = ({ quote, setQuote, showErrors, errorCodes = [''] }) => {
  const [selectedLimit, setSelectedLimit] = useState<PolicyLimitsLiabilityType | undefined>(
    () => findLiability(quote?.liabilityLimits, getPolicyType(quote?.policyType))
  )

  const limitOptionValue = (limit: PolicyLimitsLiabilityType) => {
    return `${limit.perClaim};${limit.aggregate}`
  }

  const handleSelectLimit = (value: string | undefined) => {
    const limit = findLiability(value, getPolicyType(quote?.policyType))
    setSelectedLimit(limit ?? undefined)
    setQuote({
      ...quote,
      liabilityLimits: value,
      deductible: quote?.policyType === 'BASIC' ? regularPolicyDeductibles[0] : undefined
    })
  }

  const tliePolicyUrl = quote?.policyType === 'REGULAR' ?
    tlieSources.quotePolicyRegular :
    tlieSources.quotePolicyBasic

    

    const isFormValid = useMemo(() => {
      try {
        AttorneyQuoteBoxSchema.validateSync(quote)
        return true
      } catch(e) {
        return false
      }
    }, [quote, AttorneyQuoteBoxSchema])

    const isInputIncorrect = () => {
      return showErrors && !isFormValid
    }

  return (
    <QuoteDetailBoxContainer>
      <h5>{quote.policyType === 'REGULAR' ? 'Premiere' : 'Essential'} Policy Quote</h5>
      <hr />
      <QuoteDetailBoxContentDiv>
        <div className='quote-box-left'>
          <StandardSelect
            label='Limits of Liability'
            value={selectedLimit ? limitOptionValue(selectedLimit) : ""}
            onChange={e => handleSelectLimit(e.target.value)}
            isIncorrect={isInputIncorrect() && !quote?.liabilityLimits}
            errorMessage={isInputIncorrect() && !quote?.liabilityLimits ? 'This is a required field' : ''}
            errorCode={errorCodes[0]}
          >
            <option value="" disabled>Select</option>
            {!!quote?.policyType ? liabilityLimitsPolicies[getPolicyType(quote.policyType)].map((limit, index) => (
              <option
                key={index}
                value={limitOptionValue(limit)}
              >
                {numberToCurrency(limit.perClaim, 0)} per claim / {numberToCurrency(limit.aggregate, 0)} aggregate
              </option>
            )) : null}
          </StandardSelect>
          {!!selectedLimit ? (quote?.policyType === 'REGULAR' ? (
            <FlexDiv gap="24px" margin='24px 0 0 0'>
              <LeftBar />
              <FlexDiv dir="column" gap="16px" maxWidth='410px'>
                <FormInputTitle sub>Please select a deductible option</FormInputTitle>
                {regularPolicyDeductibles.slice(0, selectedLimit?.deductibleOptions).map((deductible, index) => (
                  <StandardRadio
                    key={index}
                    label={deductible}
                    variant="outlined"
                    checked={quote?.deductible === deductible}
                    onChange={() => setQuote({ ...quote, deductible })}
                    isIncorrect={isInputIncorrect() && !quote?.deductible}
                    errorCode={errorCodes[1]}
                  />
                ))}
              </FlexDiv>
            </FlexDiv>
          ) : (
            <FlexDiv gap="24px" margin='24px 0 0 0' height='fit-content'>
              <LeftBar />
              <FlexDiv padding="10px 0">
                <strong>The Essential Policy has a fixed deductible of $1,000.</strong>
              </FlexDiv>
            </FlexDiv>
          )) : null}
        </div>
        {!!quote?.deductible ? (
          <div className='quote-box-right'>
            <div className='quote-box-icon'>
              <img src={lightbulb} alt="Light Bulb" />
            </div>
            <h6>Policy Includes</h6>
            {quote?.policyType ? policiesFeaturesIncluded[getPolicyType(quote.policyType)].map((feat, index) => (
              <Chip key={index}>
                <IoIosCheckmarkCircle />
                {feat}
              </Chip>
            )) : null}
            <Link
              href={tliePolicyUrl}
              target="_blank"
              rel="noreferrer noopener"
              style={{marginTop: '45px'}}
            >
              Learn more about this policy
            </Link>
            {getPolicyType(quote?.policyType) === 'BASIC' ? (
              <small>*Must meet qualifications for discounted rates, see full policy for details</small>
            ) : null}
          </div>
        ) : null}
      </QuoteDetailBoxContentDiv>
      <p>
        <strong>Disclaimer: </strong>
        selecting a quote does not guarantee that you will insured. Please contact our offices if you have any questions.
        {' '}
        <Link href={`tel:${tlieSources.phone}`}>{tlieSources.formattedPhone}</Link>
      </p>
    </QuoteDetailBoxContainer>
  )
}

export default QuoteDetailBox